import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MessageCircle, RefreshCw, Send, User, Bot, Radio } from "lucide-react";

const API = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "";

function fmt(ts) {
  if (!ts) return "";
  try { return new Date(ts).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ts; }
}

const INTENT_COLORS = {
  book_meeting:  "bg-purple-100 text-purple-700",
  pricing:       "bg-amber-100 text-amber-700",
  human_needed:  "bg-red-100 text-red-700",
  faq:           "bg-cyan-100 text-cyan-700",
  small_talk:    "bg-slate-100 text-slate-600",
  opt_out:       "bg-slate-200 text-slate-500",
  crm_reply:     "bg-emerald-100 text-emerald-700",
};

export default function AdminSbtWhatsApp() {
  const [conversations, setConversations] = useState([]);
  const [config, setConfig] = useState({});
  const [activeCid, setActiveCid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  const token = () => localStorage.getItem("token");
  const hdr = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${token()}` });

  const loadList = async () => {
    setLoading(true);
    try {
      const [c, cfg] = await Promise.all([
        fetch(`${API}/api/admin/whatsapp/conversations`, { headers: hdr() }),
        fetch(`${API}/api/admin/whatsapp/config`,         { headers: hdr() }),
      ]);
      const cj = await c.json();
      setConversations(cj.conversations || []);
      setConfig(await cfg.json());
    } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  };

  const loadThread = async (cid) => {
    try {
      const r = await fetch(`${API}/api/admin/whatsapp/conversations/${cid}/messages`, { headers: hdr() });
      const j = await r.json();
      setMessages(j.messages || []);
      setActiveConvo(j.conversation || null);
      setActiveCid(cid);
      setTimeout(() => scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }), 100);
    } catch { toast.error("Load thread failed"); }
  };

  useEffect(() => { loadList(); }, []);
  useEffect(() => {
    // Poll every 15s for new conversations
    const t = setInterval(loadList, 15000);
    return () => clearInterval(t);
  }, []);

  const send = async () => {
    if (!reply.trim() || !activeCid) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/api/admin/whatsapp/conversations/${activeCid}/reply`, {
        method: "POST",
        headers: hdr(),
        body: JSON.stringify({ body: reply }),
      });
      if (!r.ok) throw new Error(await r.text());
      setReply("");
      await loadThread(activeCid);
      toast.success("Sent");
    } catch (e) { toast.error("Send failed: " + e.message); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-sbt-whatsapp">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-emerald-600" /> WhatsApp Inbox
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            {config.configured
              ? <>Twilio number <code className="text-xs px-1 rounded bg-slate-100 text-slate-800">{config.twilio_number}</code> · admin relay <code className="text-xs px-1 rounded bg-slate-100 text-slate-800">{config.admin_number}</code>
                {config.signature_validation
                  ? <span className="ml-2 inline-flex items-center text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">🔒 Signature-verified</span>
                  : <span className="ml-2 inline-flex items-center text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">⚠ Unsigned</span>}
              </>
              : <span className="text-red-500">⚠ Twilio not configured — set TWILIO_ACCOUNT_SID / TWILIO_AUTH_TOKEN / TWILIO_WHATSAPP_FROM in backend env.</span>
            }
          </p>
        </div>
        <Button variant="outline" onClick={loadList} data-testid="wa-refresh">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {config.webhook_url_hint && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
          <strong>Webhook setup:</strong> {config.webhook_url_hint}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4">
        {/* ── Conversations list ── */}
        <div className="border rounded-lg bg-white overflow-hidden">
          <div className="px-3 py-2 text-xs font-semibold text-slate-600 border-b bg-slate-50">
            Conversations ({conversations.length})
          </div>
          <div className="max-h-[560px] overflow-y-auto">
            {loading && <div className="text-xs text-slate-400 italic text-center py-6">Loading…</div>}
            {!loading && conversations.length === 0 && (
              <div className="text-xs text-slate-400 italic text-center py-10 px-3">
                No conversations yet. When a visitor messages the Twilio WhatsApp number, they&apos;ll appear here.
              </div>
            )}
            {conversations.map((c) => (
              <button
                key={c.id}
                onClick={() => loadThread(c.id)}
                className={`w-full text-left px-3 py-2.5 border-b transition ${activeCid === c.id ? "bg-cyan-50" : "hover:bg-slate-50"}`}
                data-testid={`convo-${c.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-slate-900 truncate">
                    {c.visitor_name || c.visitor_phone}
                  </span>
                  {c.unread_admin > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
                      {c.unread_admin}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                  {c.last_intent && (
                    <span className={`inline-flex text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${INTENT_COLORS[c.last_intent] || "bg-slate-100 text-slate-600"}`}>
                      {c.last_intent.replace(/_/g, " ")}
                    </span>
                  )}
                  <span className="truncate">{c.visitor_phone}</span>
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">{fmt(c.last_message_at)}</div>
              </button>
            ))}
          </div>
        </div>

        {/* ── Message thread ── */}
        <div className="border rounded-lg bg-white flex flex-col min-h-[560px] max-h-[560px]">
          {!activeCid && (
            <div className="flex-1 flex items-center justify-center text-xs text-slate-400 italic px-6 text-center">
              Select a conversation to view messages.
            </div>
          )}
          {activeCid && activeConvo && (
            <>
              <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    {activeConvo.visitor_name || activeConvo.visitor_phone}
                  </div>
                  <div className="text-xs text-slate-500">{activeConvo.visitor_phone}</div>
                </div>
                {activeConvo.last_intent && (
                  <span className={`inline-flex text-[10px] uppercase font-bold px-2 py-0.5 rounded ${INTENT_COLORS[activeConvo.last_intent] || "bg-slate-100 text-slate-600"}`}>
                    {activeConvo.last_intent.replace(/_/g, " ")}
                  </span>
                )}
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
                {messages.map((m) => {
                  const isVisitor = m.direction === "in";
                  const isAdmin   = m.direction === "admin_out" || m.direction === "admin_in";
                  const isBot     = m.direction === "out" && m.ai_intent && m.ai_intent !== "crm_reply";
                  const align = isVisitor ? "justify-start" : "justify-end";
                  const bubble = isVisitor
                    ? "bg-white border border-slate-200 text-slate-800"
                    : isBot
                      ? "bg-emerald-100 border border-emerald-200 text-emerald-900"
                      : isAdmin
                        ? "bg-amber-100 border border-amber-200 text-amber-900"
                        : "bg-cyan-600 text-white";
                  const Icon = isBot ? Bot : isAdmin ? Radio : User;
                  return (
                    <div key={m.id} className={`flex ${align}`}>
                      <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${bubble}`}>
                        <div className="flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider opacity-70 mb-1">
                          <Icon className="w-3 h-3" />
                          {isVisitor ? "Visitor" : isBot ? "AI Concierge" : isAdmin ? "Admin (WhatsApp)" : "CRM Reply"}
                          {m.ai_intent && m.ai_intent !== "crm_reply" && (
                            <span className="ml-1">· {m.ai_intent.replace(/_/g, " ")}</span>
                          )}
                        </div>
                        <div className="whitespace-pre-wrap break-words">{m.body}</div>
                        <div className="text-[10px] opacity-60 mt-1">{fmt(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="p-3 border-t bg-white flex gap-2">
                <Input
                  placeholder="Type your reply — sends via Twilio to the visitor's WhatsApp"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                  data-testid="wa-reply-input"
                />
                <Button onClick={send} disabled={sending || !reply.trim()} data-testid="wa-reply-send">
                  <Send className="w-4 h-4 mr-1" /> {sending ? "Sending…" : "Send"}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
